import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.api';
import { Card } from '../ui/card';

interface TargetCount {
  clickTarget: string;
  count: number;
}

interface DailyTargetCount {
  date: string;
  clickTarget: string;
  count: number;
}

interface ClickStatsResponse {
  totalCounts: TargetCount[];
  periodCounts: TargetCount[];
  dailyTrend: DailyTargetCount[];
}

const TARGET_LABELS: Record<string, string> = {
  // 탭바 (하단 4개)
  TAB_ATTENDANCE: '출석체크',
  TAB_POPULAR: '인기설문',
  TAB_MYQUESTION: '내 설문',
  TAB_DASHBOARD: '홈',
  // 페이지 내 탭
  TAB_UNVOTED: '미참여설문',
  TAB_RANKING: '랭킹',
  // 혜택 페이지
  NAV_BENEFIT: '혜택 버튼',
  BENEFIT_ATTENDANCE: '혜택→출석',
  BENEFIT_VOTE: '혜택→투표',
  BENEFIT_CREATE: '혜택→설문작성',
  BENEFIT_COMMENT: '혜택→댓글',
  BENEFIT_LEADERBOARD: '혜택→리더보드',
  // 공지
  NOTICE: '공지사항',
};

const TARGET_GROUPS: { label: string; color: string; targets: string[] }[] = [
  {
    label: '탭바',
    color: 'blue',
    targets: ['TAB_DASHBOARD', 'TAB_ATTENDANCE', 'TAB_POPULAR', 'TAB_MYQUESTION'],
  },
  {
    label: '페이지 내 탭',
    color: 'indigo',
    targets: ['TAB_UNVOTED', 'TAB_RANKING'],
  },
  {
    label: '혜택 페이지',
    color: 'orange',
    targets: ['NAV_BENEFIT', 'BENEFIT_ATTENDANCE', 'BENEFIT_VOTE', 'BENEFIT_CREATE', 'BENEFIT_COMMENT', 'BENEFIT_LEADERBOARD'],
  },
  {
    label: '공지',
    color: 'gray',
    targets: ['NOTICE'],
  },
];

const getLabel = (target: string) => {
  if (target.startsWith('NOTICE_VIEW_')) return `공지#${target.replace('NOTICE_VIEW_', '')} 조회`;
  return TARGET_LABELS[target] || target;
};

const getGroup = (target: string): string => {
  if (target.startsWith('NOTICE_VIEW_')) return '공지';
  for (const group of TARGET_GROUPS) {
    if (group.targets.includes(target)) return group.label;
  }
  return '기타';
};

const GROUP_COLORS: Record<string, string> = {
  '탭바': 'bg-blue-50 border-blue-200',
  '페이지 내 탭': 'bg-indigo-50 border-indigo-200',
  '혜택 페이지': 'bg-orange-50 border-orange-200',
  '공지': 'bg-gray-50 border-gray-200',
  '기타': 'bg-purple-50 border-purple-200',
};

const GROUP_HEADER_COLORS: Record<string, string> = {
  '탭바': 'text-blue-700',
  '페이지 내 탭': 'text-indigo-700',
  '혜택 페이지': 'text-orange-700',
  '공지': 'text-gray-600',
  '기타': 'text-purple-700',
};

const groupItems = (items: TargetCount[]) => {
  const grouped: Record<string, TargetCount[]> = {};
  items.forEach((item) => {
    const group = getGroup(item.clickTarget);
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(item);
  });
  // 정해진 순서로 반환
  const order = ['탭바', '페이지 내 탭', '혜택 페이지', '공지', '기타'];
  return order
    .filter((g) => grouped[g])
    .map((g) => ({ group: g, items: grouped[g] }));
};

export const ClickManagement = () => {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);

  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState(today);

  const { data: stats, isLoading } = useQuery<ClickStatsResponse>({
    queryKey: ['clickStats', from, to],
    queryFn: () => adminApi.getClickStats(from, to).then(res => {
      const d = res.data;
      return (d as any).results ?? d;
    }),
  });

  // 일별 추이를 날짜별로 그룹핑
  const dailyMap = new Map<string, Record<string, number>>();
  const allTargets = new Set<string>();

  stats?.dailyTrend?.forEach((item) => {
    allTargets.add(item.clickTarget);
    if (!dailyMap.has(item.date)) {
      dailyMap.set(item.date, {});
    }
    dailyMap.get(item.date)![item.clickTarget] = item.count;
  });

  const sortedDates = Array.from(dailyMap.keys()).sort();
  const sortedTargets = Array.from(allTargets).sort();

  return (
    <div className="space-y-6">
      {/* 헤더 + 기간 필터 */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-bold text-gray-900">클릭 관리</h2>
        <div className="flex items-center gap-2 text-sm">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="border rounded px-2 py-1"
          />
          <span>~</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="border rounded px-2 py-1"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-gray-500">로딩 중...</div>
      ) : (
        <>
          {/* 전체 / 기간 집계 카드 — 그룹별 분리 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 전체 누적 */}
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 mb-3">전체 누적</h3>
              <div className="space-y-4">
                {groupItems(stats?.totalCounts || []).map(({ group, items }) => (
                  <div key={group} className={`rounded-lg border p-3 ${GROUP_COLORS[group] || ''}`}>
                    <div className={`text-xs font-bold mb-2 ${GROUP_HEADER_COLORS[group] || ''}`}>{group}</div>
                    <div className="space-y-1.5">
                      {items.map((item) => (
                        <div key={item.clickTarget} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">{getLabel(item.clickTarget)}</span>
                          <span className="text-sm font-bold text-gray-900">{item.count.toLocaleString()}회</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {(!stats?.totalCounts || stats.totalCounts.length === 0) && (
                  <div className="text-sm text-gray-400">데이터 없음</div>
                )}
              </div>
            </Card>

            {/* 기간 집계 */}
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 mb-3">
                기간 집계 ({from} ~ {to})
              </h3>
              <div className="space-y-4">
                {groupItems(stats?.periodCounts || []).map(({ group, items }) => (
                  <div key={group} className={`rounded-lg border p-3 ${GROUP_COLORS[group] || ''}`}>
                    <div className={`text-xs font-bold mb-2 ${GROUP_HEADER_COLORS[group] || ''}`}>{group}</div>
                    <div className="space-y-1.5">
                      {items.map((item) => (
                        <div key={item.clickTarget} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">{getLabel(item.clickTarget)}</span>
                          <span className="text-sm font-bold text-gray-900">{item.count.toLocaleString()}회</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {(!stats?.periodCounts || stats.periodCounts.length === 0) && (
                  <div className="text-sm text-gray-400">데이터 없음</div>
                )}
              </div>
            </Card>
          </div>

          {/* 일별 추이 테이블 */}
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 mb-3">일별 추이</h3>
            </div>
            {sortedDates.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">기간 내 데이터 없음</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50/50">
                      <th className="px-4 py-3 text-left font-medium text-gray-500">날짜</th>
                      {sortedTargets.map((t) => (
                        <th key={t} className="px-4 py-3 text-right font-medium text-gray-500">
                          {getLabel(t)}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-right font-medium text-gray-500">합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDates.map((date) => {
                      const row = dailyMap.get(date)!;
                      const total = sortedTargets.reduce((sum, t) => sum + (row[t] || 0), 0);
                      return (
                        <tr key={date} className="border-b hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-2.5 text-gray-700">{date}</td>
                          {sortedTargets.map((t) => (
                            <td key={t} className="px-4 py-2.5 text-right text-gray-900">
                              {(row[t] || 0).toLocaleString()}
                            </td>
                          ))}
                          <td className="px-4 py-2.5 text-right font-bold text-gray-900">
                            {total.toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};
